import Loader from './Loader';
import PlayerState from './PlayerState';
import { toJSON, fromJSON } from 'flatted';

/**
 * A wrapper around RadiantMP meant to aid in using RadiantMP as embedded iframe
 * player. The RadiantMP instance is controlled by passing messages to the
 * iframe's window. A message can contain a command for the wrapper to execute.
 *
 * @param  {Element}  $node  The node that should be used by RadiantMP
 * @public
 */
const Player = function ($node, config) {
    // Public states
    this.state = new PlayerState();

    // Internal state
    this.loader = new Loader();
    this.offsetStartPosition = 0;
    this.shouldShowControls = true;
    this.sendAutoplayModeDetectedOnReady = false;
    this.duration = -1;
    this.cursor = -1;

    // Player
    this.player;
    this.$node = $node;
    this.originWhitelist = config.originWhitelist;
    this.pendingAdChanges;
    this.smartclipPixel;
    this.adTagUrl;
    this.adsManager;

    // Bind callbacks
    this.ensureFocus = this.ensureFocus.bind(this);
    this.onAdBlock = this.onAdBlock.bind(this);
    this.onAdComplete = this.onAdComplete.bind(this);
    this.onAdContentPauseRequested = this.onAdContentPauseRequested.bind(this);
    this.onAdContentResumeRequested =
        this.onAdContentResumeRequested.bind(this);
    this.onAdError = this.onAdError.bind(this);
    this.onAdPaused = this.onAdPaused.bind(this);
    this.onAdPlaying = this.onAdPlaying.bind(this);
    this.onAutoplayModeDetected = this.onAutoplayModeDetected.bind(this);
    this.onBuffer = this.onBuffer.bind(this);
    this.onDurationChange = this.onDurationChange.bind(this);
    this.onEnded = this.onEnded.bind(this);
    this.onError = this.onError.bind(this);
    this.onLoadedMetadata = this.onLoadedMetadata.bind(this);
    this.onLoaderStateChanged = this.onLoaderStateChanged.bind(this);
    this.onMessage = this.onMessage.bind(this);
    this.onPause = this.onPause.bind(this);
    this.onPlay = this.onPlay.bind(this);
    this.onReady = this.onReady.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onSeeked = this.onSeeked.bind(this);
    this.onSeeking = this.onSeeking.bind(this);
    this.onStopCompleted = this.onStopCompleted.bind(this);
    this.onTimeUpdate = this.onTimeUpdate.bind(this);
    this.onWarning = this.onWarning.bind(this);

    // Start listening for commands
    window.addEventListener('message', this.onMessage);

    // Load Radiant Media Player
    this.loader
        .load()
        .then(this.onLoaderStateChanged)
        .catch(function () {
            if (import.meta.env.DEV) {
                console.log(error);
            }
            return;
        });
};

// State Changes
// --------------------------------

/**
 * Responds to loader state change by broadcasting it.
 *
 * @internal
 */
Player.prototype.onLoaderStateChanged = function () {
    this.broadcastLoaderState();
};

/**
 * Updates the `state` and broadcasts the new state.
 *
 * @private
 */
Player.prototype.updateState = function (state) {
    // Verify if state actually changed
    if (JSON.stringify(state) !== JSON.stringify(this.state)) {
        this.state = state;

        if (state.playerState !== 'idle' && state.playerState !== 'ended') {
            if (!this.$node.classList.contains('no-poster')) {
                this.$node.classList.add('no-poster');
            }
        } else {
            while (this.$node.classList.contains('no-poster')) {
                this.$node.classList.remove('no-poster');
            }
        }
        this.broadcastPlayerState();
    }
};

// Handle Messages
// --------------------------------

/**
 * Handles incoming messages, ignores them if they don't come from a whitelisted
 * origin. If the incoming message appears to be a command, we pass it to the
 * command execution method.
 *
 * @param  {Event}  event  The message event
 * @internal
 */
Player.prototype.onMessage = function (event) {
    let command, args;

    // Origin check
    if (!this.originWhitelist.includes(event.origin)) {
        console.warn(
            `[radiant-mediaplayer-iframe] Rejected message from non-whitelisted origin: ${event.origin}. Allowed origins:`,
            this.originWhitelist
        );
        return;
    }

    // Type check
    if (
        !event.data ||
        typeof event.data !== 'object' ||
        event.data.type !== 'RADIANT_EMBED_COMMAND' ||
        !event.data.command ||
        typeof event.data.command !== 'string'
    ) {
        return;
    }

    try {
        command = event.data.command;
        args = fromJSON(event.data.args);
        this.executeCommand(command, args);
    } catch (error) {
        if (import.meta.env.DEV) {
            console.log(error);
        }
        return;
    }
};

/**
 * Receives a command and executes it if it's a known command. Broadcasts an
 * error otherwise.
 *
 * @param  {string}  command  The command
 * @param  {array=}  args     An optional array of arguments for the command
 * @private
 */
Player.prototype.executeCommand = function (command, args) {
    switch (command) {
        case 'broadcastLoaderState':
        case 'broadcastPlayerState':
        case 'changeMedia':
        case 'destroy':
        case 'getAutoplayMode':
        case 'getCursor':
        case 'getDuration':
        case 'hideControls':
        case 'isShowingControls':
        case 'pause':
        case 'play':
        case 'requestReinitializeVolume':
        case 'seek':
        case 'setPlayerSize':
        case 'setup':
        case 'showControls':
        case 'stop':
            return this[command].apply(this, args ?? []);
        default:
            return this.sendEvent({
                type: 'playerError',
                message: `Unknown command: ${String(command)}`,
                error: new Error(`Unknown command: ${String(command)}`),
            });
    }
};

// Send Events
// --------------------------------

/**
 * Sends the event as postMessage to the parent document.
 *
 * @param  {Object}  event  The event object
 * @private
 */
Player.prototype.sendEvent = function (event) {
    window.parent.postMessage(
        {
            type: 'RADIANT_EMBED_EVENT',
            event: toJSON(event),
        },
        '*'
    );
};

// Player Instantiation
// --------------------------------

/**
 * Set up the Radiant Media Player instance and binds event listeners.
 *
 * @param  {Object}  setup  The setup to pass to the new RadiantMP instance
 * @private
 */
Player.prototype.setup = function (setup) {
    // Vendor files
    setup.pathToRmpFiles = './radiant/';

    // Logo
    if (setup.logo) {
        this.logoWidth = setup.logoWidth || 64;
        this.logoHeight = setup.logoHeight || 64;
        this.logoMinWidth = setup.logoMinWidth || 24;
        this.logoMaxWidth = setup.logoMaxWidth || 64;
        this.logoMinHeight = setup.logoMinHeight || 24;
        this.logoMaxHeight = setup.logoMaxHeight || 64;
    }

    // Custom fonts
    if (setup.fonts) {
        setup.fonts.ui && this.setupFont(setup.fonts.ui, 'ui');
        setup.fonts.cc && this.setupFont(setup.fonts.cc, 'cc');
    }

    // Setup icon map
    if (!setup.customIconsLoc) {
        setup.customIconsLoc = './assets/icons/icons.json';
    }

    // Instantiate Player
    this.player = new RadiantMP(this.$node.id);

    // Bind listeners
    this.player.on('adblock', this.onAdBlock);
    this.player.on('adcomplete', this.onAdComplete);
    this.player.on('adcontentpauserequested', this.onAdContentPauseRequested);
    this.player.on('adcontentresumerequested', this.onAdContentResumeRequested);
    this.player.on('aderror', this.onAdError);
    this.player.on('adpaused', this.onAdPaused);
    this.player.on('adresumed', this.onAdPlaying);
    this.player.on('adstarted', this.onAdPlaying);
    this.player.on('autoplaymodedetected', this.onAutoplayModeDetected);
    this.player.on('bufferstalled', this.onBuffer);
    this.player.on('durationchange', this.onDurationChange);
    this.player.on('ended', this.onEnded);
    this.player.on('error', this.onError);
    this.player.on('loadedmetadata', this.onLoadedMetadata);
    this.player.on('pause', this.onPause);
    this.player.on('play', this.onPlay);
    this.player.on('ready', this.onReady);
    this.player.on('seeked', this.onSeeked);
    this.player.on('seeking', this.onSeeking);
    this.player.on('stopcompleted', this.onStopCompleted);
    this.player.on('timeupdate', this.onTimeUpdate);
    this.player.on('warning', this.onWarning);

    // React to window resize
    window.addEventListener('resize', this.onResize);

    // Ensure the player receives focus on keyup, so that the player will
    // respond to the key command. If the player doesn't have focus, it won't
    // respond.
    document.addEventListener('keyup', this.ensureFocus);

    // Autocomplete setup
    setup.offsetStartPosition = setup.offsetStartPosition ?? 0;
    this.offsetStartPosition = setup.offsetStartPosition;

    // Set player safe area
    this.setSafeArea(
        setup.safeAreaInsetTop,
        setup.safeAreaInsetRight,
        setup.safeAreaInsetBottom,
        setup.safeAreaInsetLeft
    );

    // Setup smartclip pixel
    if (setup.ads && setup.smartclipPixel) {
        this.setSmartclipPixel(setup.smartclipPixel);
        delete setup.smartclipPixel;
    }

    // Set AdTag Url
    if (setup.ads && setup.adTagUrl) {
        this.adTagUrl = setup.adTagUrl;
    }

    this.player.init(setup).catch(function () {
        // Error handled via events, no need to propagate.
        if (import.meta.env.DEV) {
            console.log(arguments);
        }
    });
};

/**
 * Setup a custom font.
 *
 * Config properties:
 *  - `import`  A url to load the font. It'll be set as css @import url.
 *  - `var`     A string value for the css var that stores the font family.
 *
 * @param  {object}  config  The font configuration
 * @param  {string}  type    The type of font (cc or ui)
 * @private
 */
Player.prototype.setupFont = function (config, type) {
    let $import, importUrl;

    if (config.import) {
        try {
            $import = document.createElement('style');
            importUrl = new URL(config.import);
            $import.innerHTML = "@import url('" + importUrl.toString() + "');";
        } catch (error) {
            if (import.meta.env.DEV) {
                console.error(error);
            }
        }
    }

    if (config.var) {
        switch (type) {
            case 'cc':
                document.body.style.setProperty('--font-family-cc', config.var);
                break;
            case 'ui':
                document.body.style.setProperty('--font-family-ui', config.var);
                break;
            default:
                break;
        }
    }
};

/**
 * Injects a pixel for smartclip.
 *
 * @param  {string}  pixel  The pixel url
 * @private
 */
Player.prototype.setSmartclipPixel = function (pixel) {
    let script;

    this.removeSmartclipPixel();

    script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = pixel;
    document.getElementsByTagName('head')[0].appendChild(script);

    this.smartclipPixel = script;
};

/**
 * Removes the smartclip pixel if present.
 *
 * @private
 */
Player.prototype.removeSmartclipPixel = function () {
    if (this.smartclipPixel) {
        this.smartclipPixel.remove();
        this.smartclipPixel = null;
    }
};

/**
 * Radiant only responds to keyboard events if a control or the video tag has
 * focus. Focusing on play button appears to be the most reliable at the moment.
 *
 * @private
 */
Player.prototype.ensureFocus = function () {
    let control;

    if (document.activeElement === document.body) {
        control = document.querySelector('.rmp-control-bar .rmp-play-pause');
        control && control.focus();
    }
};

/**
 * This handler is used for the `ready` event.
 *
 * @internal
 */
Player.prototype.onReady = function () {
    let state;

    if (this.state.isReady !== 1) {
        // Update state
        state = this.state.clone();
        state.isReady = 1;
        this.updateState(state);

        // Update controls visibility
        this.shouldShowControls ? this.showControls() : this.hideControls();
    }
};

// Player Source Changing
// --------------------------------

/**
 * Set new media source and contentMetaData.
 *
 * @param  {Object}   src                  The new media source
 * @param  {Object}   contentMetaData      The metadata of the new media
 * @param  {boolean}  autoplay             Whether to play after srcChange
 * @param  {number=}  offsetStartPosition  The optional start position
 * @param  {Object=}  shakaDrm             The optional shakaDrm settings
 * @param  {Object=}  adSetup              The optional ads settings for the new
 *                                         media
 * @private
 */
Player.prototype.changeMedia = function (
    src,
    contentMetadata,
    autoplay,
    offsetStartPosition,
    shakaDrm,
    adSetup
) {
    let that = this,
        player = this.player,
        error,
        state,
        playerStopPromise,
        playerStopAdsPromise;

    if (!this.state.isSrcChanging === 1) {
        return this.sendEvent({
            type: 'playerWarning',
            message: `Player is still changing to another source`,
        });
    }

    if (!this.state.isReady === 1) {
        return this.sendEvent({
            type: 'playerWarning',
            message: `Player is not ready. Current state: ${state.isReady}`,
        });
    }

    if (!src) {
        return this.sendEvent({
            type: 'playerWarning',
            message: `Source required`,
        });
    }

    if (!contentMetadata) {
        return this.sendEvent({
            type: 'playerWarning',
            message: `ContentMetadata required`,
        });
    }

    adSetup = adSetup || { ads: false };

    // Set srcchanging state
    state = this.state.clone();
    state.isReady = 0;
    state.hasStarted = false;
    state.isSrcChanging = true;
    state.playerState = 'srcchanging';
    this.updateState(state);

    // Stop Current Media & Ads
    playerStopPromise = player.stop();
    this.removeSmartclipPixel();

    this.adTagUrl = null;
    playerStopAdsPromise = this.state.isPlayingAds
        ? player.stopAds()
        : Promise.resolve();

    Promise.all([playerStopPromise, playerStopAdsPromise])

        // Setup new Media & Ads
        .then(function () {
            let setSrcPromise, loadAdsPromise;

            // We should seek to this position once metadata is loaded. This
            // is currently handled by the `onLoadedMetadata()` method.
            that.offsetStartPosition = offsetStartPosition;

            // Update media
            player.contentMetadataConfiguration = contentMetadata;
            player.shakaDrmConfiguration = shakaDrm || {};
            setSrcPromise = player.setSrc(src);

            // Update ads
            if (adSetup.ads && adSetup.smartclipPixel) {
                that.setSmartclipPixel(adSetup.smartclipPixel);
            }

            if (adSetup.ads && adSetup.adTagUrl) {
                that.adTagUrl = adSetup.adTagUrl;
                loadAdsPromise = player
                    .loadAds(adSetup.adTagUrl)
                    .catch(function (error) {
                        if (import.meta.env.DEV) {
                            console.log(error);
                        }
                        return Promise.resolve();
                    });
            } else {
                loadAdsPromise = Promise.resolve();
            }

            return Promise.all([setSrcPromise, loadAdsPromise]);
        })

        // Source change completed
        .then(function () {
            let state;

            // Set `srcchanged` state
            // Update player state
            state = that.state.clone();
            state.isReady = 1;
            state.isSrcChanging = false;
            state.playerState = 'srcchanged';
            that.updateState(state);
        })
        .catch(function () {
            // Error handled via events, no need to propagate.
            if (import.meta.env.DEV) {
                console.log(arguments);
            }
        });
};

// Player Destruction
// --------------------------------

/**
 * Destroy player
 *
 * @private
 */
Player.prototype.destroy = function () {
    let player = this.player;

    if (player) {
        player.stop();

        this.smartclipPixel && this.removeSmartclipPixel();
        this.state.isPlayingAds && player.stopAds();

        player
            .destroy()
            .then(function () {
                // Unbind listeners
                player.off('adblock', this.onAdBlock);
                player.off('adcomplete', this.onAdComplete);
                player.off(
                    'adcontentpauserequested',
                    this.onAdContentPauseRequested
                );
                player.off(
                    'adcontentresumerequested',
                    this.onAdContentResumeRequested
                );
                player.off('aderror', this.onAdError);
                player.off('adpaused', this.onAdPaused);
                player.off('adresumed', this.onAdPlaying);
                player.off('adstarted', this.onAdPlaying);
                player.off('autoplaymodedetected', this.onAutoplayModeDetected);
                player.off('bufferstalled', this.onBuffer);
                player.off('durationchange', this.onDurationChange);
                player.off('ended', this.onEnded);
                player.off('error', this.onError);
                player.off('loadedmetadata', this.onLoadedMetadata);
                player.off('pause', this.onPause);
                player.off('play', this.onPlay);
                player.off('ready', this.onReady);
                player.off('seeked', this.onSeeked);
                player.off('seeking', this.onSeeking);
                player.off('stopcompleted', this.onStopCompleted);
                player.off('timeupdate', this.onTimeUpdate);
                player.off('warning', this.onWarning);

                // Stop reacting to window resize
                window.removeEventListener('resize', this.onResize);

                // Stop capturing focus
                document.removeEventListener('keyup', this.ensureFocus);

                // Update PlayerState
                state = new PlayerState();
                state.isReady = -1;
                state.hasStarted = false;
                state.isPlaying = false;
                state.isSeekable = false;
                state.playerState = 'destroyed';
                this.updateState(state);

                // Done
                this.sendEvent({
                    type: 'destroyed',
                    state: this.state,
                });
            })
            .catch((error) => {
                // An error/warning event will also be triggered. For now we'll
                // be using the events.
                if (import.meta.env.DEV) {
                    console.log(error);
                }
                return;
            });

        // Clear player instance
        this.player = null;

        // Update PlayerState
        state = that.state.clone();
        state.playerState = 'destroying';
        this.updateState(state);

        // Done
        this.sendEvent({
            type: 'destroying',
            state: this.state,
        });
    }
};

// Broadcast State
// --------------------------------

/**
 * Sends an event that contains the current loader state.
 *
 * @private
 */
Player.prototype.broadcastLoaderState = function () {
    this.sendEvent({
        type: 'loaderStateChanged',
        state: this.loader.state,
    });
};

/**
 * Sends an event that contains the current player state.
 *
 * @private
 */
Player.prototype.broadcastPlayerState = function () {
    this.sendEvent({
        type: 'playerStateChanged',
        state: this.state,
    });
};

// Player Commands
// --------------------------------

/**
 * Shows the player controls if the player is ready. Schedules for them to be
 * shown as soon as it's ready otherwise.
 *
 * @private
 */
Player.prototype.showControls = function () {
    this.state.isReady === 1
        ? (this.player.controls = true)
        : (this.shouldShowControls = true);
};

/**
 * Hides the player controls if the player is ready. Schedules for them to be
 * hidden as soon as it's ready otherwise.
 *
 * @private
 */
Player.prototype.hideControls = function () {
    this.state.isReady === 1
        ? (this.player.controls = false)
        : (this.shouldShowControls = false);
};

/**
 * Sets the new player size.
 *
 * @param  {number}    width                The width in pixels
 * @param  {number}    height               The height in pixels
 * @param  {string=}   safeAreaInsetTop     Optional safe area inset at the top
 * @param  {string=}   safeAreaInsetRight   Optional safe area inset at the
 *                                          right
 * @param  {string=}   safeAreaInsetBottom  Optional safe area inset at the
 *                                          bottom
 * @param  {string=}   safeAreaInsetLeft    Optional safe area inset at the left
 * @private
 */
Player.prototype.setPlayerSize = function (
    width,
    height,
    safeAreaInsetTop,
    safeAreaInsetRight,
    safeAreaInsetBottom,
    safeAreaInsetLeft
) {
    this.setSafeArea(
        safeAreaInsetTop,
        safeAreaInsetRight,
        safeAreaInsetBottom,
        safeAreaInsetLeft
    );
    this.player &&
        !this.player.iframeMode &&
        !this.player.autoHeightMode &&
        this.player.setPlayerSize({
            width: width,
            height: height,
        });
    this.alignLogo();
};

/**
 * Sets the player's safe area
 *
 * @param  {string=}   safeAreaInsetTop     Optional safe area inset at the top
 * @param  {string=}   safeAreaInsetRight   Optional safe area inset at the
 *                                          right
 * @param  {string=}   safeAreaInsetBottom  Optional safe area inset at the
 *                                          bottom
 * @param  {string=}   safeAreaInsetLeft    Optional safe area inset at the left
 * @private
 */
Player.prototype.setSafeArea = function (
    safeAreaInsetTop,
    safeAreaInsetRight,
    safeAreaInsetBottom,
    safeAreaInsetLeft
) {
    document.body.style.setProperty(
        '--safe-area-inset-top',
        safeAreaInsetTop || '0px'
    );
    document.body.style.setProperty(
        '--safe-area-inset-right',
        safeAreaInsetRight || '0px'
    );
    document.body.style.setProperty(
        '--safe-area-inset-bottom',
        safeAreaInsetBottom || '0px'
    );
    document.body.style.setProperty(
        '--safe-area-inset-left',
        safeAreaInsetLeft || '0px'
    );
    if (
        safeAreaInsetTop ||
        safeAreaInsetRight ||
        safeAreaInsetBottom ||
        safeAreaInsetLeft
    ) {
        if (!this.$node.classList.contains('use-safe-area-inset')) {
            this.$node.classList.add('use-safe-area-inset');
        }
    } else {
        if (this.$node.classList.contains('use-safe-area-inset')) {
            this.$node.classList.remove('use-safe-area-inset');
        }
    }
};

/**
 * TODO: Probably useless
 * Mutes and unmutes the audio. This helps re-enable audio on IOS after screen
 * unlock, where sometimes the audio is suddenly gone.
 *
 * @private
 */
Player.prototype.requestReinitializeVolume = function () {
    let mediaElement = this.player.htmlMediaElement;

    if (mediaElement) {
        mediaElement.muted = true;
        mediaElement.muted = false;
        console.log('reinitialized volume');
    }
};

/**
 * Start playing
 *
 * @private
 */
Player.prototype.play = function () {
    let isReady = this.state.isReady;

    if (isReady !== 1) {
        return this.sendEvent({
            type: 'playerWarning',
            message: `Player is not ready. Current state: ${state.isReady}`,
        });
    }

    this.player.play().catch(function () {
        // Error handled via events, no need to propagate.
        if (import.meta.env.DEV) {
            console.log(arguments);
        }
    });
};

/**
 * Move to a certain cursor position
 *
 * @param  {number}  cursor  The cursor position, in seconds
 * @private
 */
Player.prototype.seek = function (cursor) {
    let state = this.state;

    if (state.isReady !== 1) {
        return this.sendEvent({
            type: 'playerWarning',
            message: `Player is not ready. Current state: ${state.isReady}`,
        });
    }

    if (!this.hasStarted) {
        this.offsetStartPosition = cursor * 1000;
        return;
    }

    if (!state.isSeekable) {
        return this.sendEvent({
            type: 'playerWarning',
            message: `Player is not seekable.`,
        });
    }

    this.player.seekTo(cursor * 1000).catch(function () {
        // Error handled via events, no need to propagate.
        if (import.meta.env.DEV) {
            console.log(arguments);
        }
    });
};

/**
 * Pause the player
 *
 * @private
 */
Player.prototype.pause = function () {
    if (this.state.isReady !== 1) {
        return;
    }

    // Apparently IOS really doesn't like pausing if we aren't playing.
    if (this.state.isPlaying || this.state.isPlayingAds) {
        this.player.pause();
    }
};

/**
 * Stop the player
 *
 * @private
 */
Player.prototype.stop = function () {
    let state = this.state;

    if (state.isReady !== 1) {
        return this.sendEvent({
            type: 'playerWarning',
            message: `Player is not ready. Current state: ${state.isReady}`,
        });
    }

    // Optional
    state.isPlayingAds && this.player.stopAds();

    if (state.isPlaying) {
        return this.player.stop().catch(function () {
            // Error handled via events, no need to propagate.
            if (import.meta.env.DEV) {
                console.log(arguments);
            }
        });
    }
};

/**
 * Sends a `autoplayMode` event with the autoplay mode retrieved from the
 * player.
 *
 * @private
 */
Player.prototype.getAutoplayMode = function () {
    this.player
        .getAutoplayResult()
        .then((autoplayResult) => {
            this.sendEvent({
                type: 'autoplayMode',
                autoplayMode: autoplayResult,
            });
        })
        .catch((error) => {
            if (import.meta.env.DEV) {
                console.log(error);
            }
            this.sendEvent({
                type: 'autoplayMode',
                autoplayMode: null,
                error: error,
            });
        });
};

/**
 * Sends a `cursor` event with the last known cursor position.
 *
 * @private
 */
Player.prototype.getCursor = function () {
    this.sendEvent({
        type: 'cursor',
        cursor: this.cursor,
    });
};

/**
 * Sends a `duration` event with the last known duration.
 *
 * @private
 */
Player.prototype.getDuration = function () {
    this.sendEvent({
        type: 'duration',
        duration: this.duration,
    });
};

/**
 * Sends as `isShowingControls` event to tell if controls are showing.
 *
 * @private
 */
Player.prototype.isShowingControls = function () {
    this.sendEvent({
        type: 'isShowingControls',
        isShowingControls: this.player.controls,
    });
};

/**
 * Stub logo alignment method. Can be replaced from the setup.
 *
 * @private
 */
Player.prototype.alignLogo = function () {
    let playerSize,
        playerWidth,
        playerHeight,
        playerAspectRatio,
        $media,
        videoAspectRatio,
        logoAspectRatio,
        logoMinWidth,
        logoMaxWidth,
        logoMinHeight,
        logoMaxHeight,
        logoWidth,
        logoHeight,
        bodyStyle,
        safeAreaInsetLeft,
        safeAreaInsetTop,
        logoOffset,
        logoLeft,
        logoTop,
        $logo;

    // Check for logo node
    this.$logo = this.$logo || document.querySelector('#player .rmp-logo');

    // No logo yet
    if (!this.$logo) {
        return;
    }

    // Player dimensions
    playerSize = this.player.getPlayerSize();
    playerWidth = playerSize.width;
    playerHeight = playerSize.height;
    playerAspectRatio = playerWidth / playerHeight;

    // Media native dimensions
    $media = this.player.htmlMediaElement;
    videoWidth = $media ? $media.videoWidth || playerWidth : playerWidth;
    videoHeight = $media ? $media.videoHeight || playerHeight : playerHeight;
    videoAspectRatio = videoWidth / videoHeight;

    // Scale video dimensions to fit within the player as needed
    if (playerAspectRatio >= videoAspectRatio) {
        videoHeight = playerHeight;
        videoWidth = videoHeight * videoAspectRatio;
    } else {
        videoWidth = playerWidth;
        videoHeight = videoWidth / videoAspectRatio;
    }

    // Logo size
    logoAspectRatio = this.logoWidth / this.logoHeight;
    logoMinWidth = this.logoMinWidth;
    logoMaxWidth = this.logoMaxWidth;
    logoMinHeight = this.logoMinHeight;
    logoMaxHeight = this.logoMaxHeight;
    if (logoAspectRatio >= 1) {
        logoWidth = Math.min(
            Math.max(videoWidth * 0.033, logoMinWidth),
            logoMaxWidth
        );
        logoHeight = logoWidth / logoAspectRatio;
    } else {
        logoHeight = Math.min(
            Math.max(videoHeight * 0.033, logoMinHeight),
            logoMaxHeight
        );
        logoWidth = logoHeight * logoAspectRatio;
    }

    // Safe area
    bodyStyle = window.getComputedStyle(document.body);
    safeAreaInsetLeft = bodyStyle.getPropertyValue('--safe-area-inset-left');
    safeAreaInsetLeft = safeAreaInsetLeft.length ? '0px' : safeAreaInsetLeft;
    safeAreaInsetTop = bodyStyle.getPropertyValue('--safe-area-inset-top');
    safeAreaInsetTop = safeAreaInsetTop.length ? '0px' : safeAreaInsetTop;

    // Logo offset
    logoOffset = Math.min(
        ((videoWidth + videoHeight) / 2) * 0.033,
        (logoMaxWidth + logoMaxHeight) / 2
    );
    logoLeft = logoOffset * 2 + (playerWidth - videoWidth) / 2;
    logoTop = logoOffset + (playerHeight - videoHeight) / 2;

    // Position and scale logo
    $logo = this.$logo;
    $logo.style.width = logoWidth + 'px';
    $logo.style.height = logoHeight + 'px';
    $logo.style.left = `calc(${logoLeft}px + ${safeAreaInsetLeft})`;
    $logo.style.top = `calc(${logoTop}px + ${safeAreaInsetTop})`;
    $logo.style.display = 'block';
};

// Handlers
// --------------------------------

/**
 * Re-broadcasts the `autoplayModeDetected` event.
 *
 * @internal
 */
Player.prototype.onAutoplayModeDetected = function () {
    this.sendEvent({
        type: 'autoplayModeDetected',
    });
};

/**
 * This handler is used for the `loadedMetadata` event. Currently just
 * re-broadcasts it. Generally when this event fires, the player is ready to
 * play.
 *
 * @internal
 */
Player.prototype.onLoadedMetadata = function () {
    if (this.offsetStartPosition) {
        this.seekTo(this.offsetStartPosition / 1000);
    }
    this.alignLogo();
    this.sendEvent({
        type: 'loadedMetadata',
    });
};

/**
 * This handler is used for the `durationchange` event. Updates internally
 * stored duration value.
 *
 * @internal
 */
Player.prototype.onDurationChange = function () {
    this.duration = this.player.duration / 1000;
    this.sendEvent({
        type: 'durationChanged',
        duration: this.duration,
    });
};

/**
 * This handler is used for the `adcontentpauserequested` event.
 *
 * @internal
 */
Player.prototype.onAdContentPauseRequested = function () {
    let state;

    state = this.state.clone();
    state.contentType = 'ads';
    this.updateState(state);
};

/**
 * This handler is used for the `adstarted` and `adresumed` events.
 *
 * @internal
 */
Player.prototype.onAdPlaying = function () {
    let state;

    state = this.state.clone();
    state.isPlayingAds = true;
    state.playerState = 'ad_playing';
    this.updateState(state);
};

/**
 * This handler is used for the `adblock` events.
 *
 * @internal
 */
Player.prototype.onAdBlock = function () {
    this.sendEvent({
        type: 'adblock',
    });
};

/**
 * Radiant MediaPlayer encountered an error with ads.
 *
 * @param  {Object}  event  The error event
 * @internal
 */
Player.prototype.onAdError = function (event) {
    let player = this.player,
        mediaElement = player ? player.htmlMediaElement : null,
        details;

    details = {
        event: event,
        adErrorType: player ? player.adErrorType : null,
        adErrorCode: player ? player.adErrorCode : null,
        adErrorMessage: player ? player.adErrorMessage : null,
        adVastErrorCode: player ? player.adVastErrorCode : null,
        mediaElementError: mediaElement ? mediaElement.error : null,
    };

    if (this.state.isReady !== 1) {
        this.updateState(new PlayerState());

        this.sendEvent({
            type: 'setupError',
            message:
                details.adErrorMessage ||
                details?.mediaElementError?.message ||
                '',
            error: details,
        });
    } else {
        this.sendEvent({
            type: 'adError',
            message:
                details.adErrorMessage ||
                details?.mediaElementError?.message ||
                '',
            error: details,
        });
    }
};

/**
 * This handler is used for the `adpaused` event.
 *
 * @internal
 */
Player.prototype.onAdPaused = function () {
    let state;

    state = this.state.clone();
    state.isPlayingAds = false;
    state.playerState = 'ad_paused';
    this.updateState(state);
};

/**
 * This handler is used for the `adcomplete` event.
 *
 * @internal
 */
Player.prototype.onAdComplete = function () {
    let state;

    state = this.state.clone();
    state.isPlayingAds = false;
    state.playerState = 'ad_complete';
    this.updateState(state);
};

/**
 * This handler is used for the `adcontentresumerequested` event.
 *
 * @internal
 */
Player.prototype.onAdContentResumeRequested = function () {
    let state;

    state = this.state.clone();
    state.contentType = 'media';
    this.updateState(state);
};

/**
 * This handler is used for the `play` event. Marks player as playing, provided
 * it has officially been marked as `started`.
 *
 * @internal
 */
Player.prototype.onPlay = function () {
    let state;

    if (this.state.hasStarted) {
        state = this.state.clone();
        state.isPlaying = true;
        state.contentType = 'media';
        state.playerState = 'playing';
        this.updateState(state);
    }
};

/**
 * This handler is used for the `stopcompleted` event. It marks the player as
 * idle.
 *
 * @internal
 */
Player.prototype.onStopCompleted = function () {
    let state;

    state = this.state.clone();
    state.isPlaying = false;
    state.playerState = 'idle';
    this.updateState(state);
};

/**
 * This handler is used for the `bufferstalled` event. It fires when the player
 * is loading new media data to play.
 *
 * @internal
 */
Player.prototype.onBuffer = function () {
    let state;

    state = this.state.clone();
    state.playerState = 'buffering';
    this.updateState(state);
};

/**
 * This handler is used for the `seeking` event. If user seeks before the
 * initial `offsetStartPosition` has been reached, we update the
 * `offsetStartPosition` with the new time.
 *
 * @internal
 */
Player.prototype.onSeeking = function () {
    let state;

    // Player hasn't officially started yet, meaning the `offsetStartPosition`
    // has not yet been reached.
    if (!this.state.hasStarted) {
        this.offsetStartPosition = this.player.currentTime;
    }

    state = this.state.clone();
    state.playerState = 'seeking';
    this.updateState(state);
};

/**
 * This handler is used for the `seeked` event. If user seeks before the initial
 * `offsetStartPosition` has been reached, we update the `offsetStartPosition`
 * with the new time.
 *
 * @internal
 */
Player.prototype.onSeeked = function () {
    let state;

    // Player hasn't officially started yet, meaning the `offsetStartPosition`
    // has not yet been reached.
    if (!this.state.hasStarted) {
        this.offsetStartPosition = this.player.currentTime;
    }

    state = this.state.clone();
    state.playerState = 'seeked';
    this.updateState(state);
};

/**
 * This handler is used for the `timeupdate` event. If the `offsetStartPosition`
 * has been reached, we'll be able to say playback has officially started.
 *
 * @internal
 */
Player.prototype.onTimeUpdate = function () {
    let currentTime, state;

    // For some reason this event can fire before onPlayerReady. If it does, we
    // ignore it, so we can properly respond to the first time update when the
    // player is guaranteed to be ready.
    if (this.state.isReady < 1) {
        return;
    }

    currentTime = this.player.currentTime;

    // Player not playing
    if (this.player.paused) {
        state = this.state.clone();
        state.isSeekable = true;
        state.isPlaying = false;

        // But also not seeking
        if (currentTime >= this.offsetStartPosition) {
            state.playerState = 'paused';
        }

        this.updateState(state);
    }

    // Player is playing
    else {
        state = this.state.clone();
        state.isSeekable = true;
        state.isPlaying = true;
        state.playerState = 'playing';

        // Which means player has officially started, too
        if (!this.state.hasStarted) {
            state.contentType = 'media';
            state.hasStarted = true;
        }

        this.updateState(state);
    }

    this.cursor = currentTime / 1000;
    this.sendEvent({
        type: 'cursorChanged',
        cursor: this.cursor,
    });
};

/**
 * This handler is used for the `pause` event. Marks player as not playing and
 * sets it's state to paused.
 *
 * @internal
 */
Player.prototype.onPause = function () {
    let state;

    state = this.state.clone();
    state.isPlaying = false;
    state.playerState = 'paused';
    this.updateState(state);
};

/**
 * This handler is used for the `ended` event. Marks player as not playing and
 * sets it's state to idle.
 *
 * @internal
 */
Player.prototype.onEnded = function () {
    let state;

    state = this.state.clone();
    state.isPlaying = false;
    state.playerState = 'ended';
    this.updateState(state);
};

/**
 * Radiant MediaPlayer encountered an error.
 *
 * @param  {Object}  event  The error event
 * @internal
 */
Player.prototype.onError = function (event) {
    let player = this.player,
        mediaElement = player ? player.htmlMediaElement : null,
        details;

    details = {
        event: event,
        playerError: player ? player.errorData : null,
        mediaElementError: mediaElement ? mediaElement.error : null,
    };

    if (this.state.isReady !== 1) {
        this.updateState(new PlayerState());

        this.sendEvent({
            type: 'setupError',
            message:
                details?.playerError?.message ||
                details?.mediaElementError?.message ||
                '',
            error: details,
        });
    } else {
        this.sendEvent({
            type: 'playerError',
            message:
                details?.playerError?.message ||
                details?.mediaElementError?.message ||
                '',
            error: details,
        });
    }
};

/**
 * Radiant MediaPlayer encountered a warning.
 *
 * @param  {Object}  event  The warning event
 * @internal
 */
Player.prototype.onWarning = function (event) {
    let player = this.player,
        mediaElement = player ? player.htmlMediaElement : null,
        details;

    details = {
        event: event,
        playerError: player ? player.warningData : null,
        mediaElementError: mediaElement ? mediaElement.error : null,
    };

    if (this.state.isReady !== 1) {
        this.updateState(new PlayerState());

        this.sendEvent({
            type: 'setupWarning',
            message:
                details?.playerError?.message ||
                details?.mediaElementError?.message ||
                '',
            error: details,
        });
    } else {
        this.sendEvent({
            type: 'playerWarning',
            message:
                details?.playerError?.message ||
                details?.mediaElementError?.message ||
                '',
            error: details,
        });
    }
};

/**
 * Respond to window resize.
 *
 * @internal
 */
Player.prototype.onResize = function () {
    this.alignLogo();
};

export default Player;
